import {
  Configuration,
  CustomerModel,
  CustomerModelODataResultCollection,
  CustomerApi,
  UserManagementApi,
  UserManagementModel,
  UserManagementModelODataResultCollection,
  UserCustomerPermissionApi,
  UserCustomerPermissionModelODataResultCollection,
} from "api";
import {
  baseApiConfig,
  apiResponseTransformer,
  TransformedApiResponse,
  invalidStatusCode,
  sortToString,
  andFilters,
  ApiResponse,
  orFilters,
} from "./apiHelpers";
import { valueOr } from "util/statHelpers";
import { CONTRACT, ESIGN, ORDERS, USER_MANAGEMENT } from "util/featureFlags";
import filterFactories from "./filterFactories";
import { SORT_DIRECTION } from "hooks/useSort";
import {
  IRM_PERMISSIONS,
  permissionsObjectToPermissionValues,
} from "services/customerPermissionsHelpers";
import { ApiStatusException } from "services/apiErrors";

class CustomerService {
  private api!: CustomerApi;
  private userManagementApi!: UserManagementApi;
  private userCustomerPermissionApi!: UserCustomerPermissionApi;

  constructor(config: Configuration = baseApiConfig) {
    this.api = new CustomerApi(config);
    this.userManagementApi = new UserManagementApi(config);
    this.userCustomerPermissionApi = new UserCustomerPermissionApi(config);
  }

  private static mapApiCustomer(apiCustomer: CustomerModel): Customer {
    return {
      currency: valueOr(apiCustomer.CurrencyCode),
      code: valueOr(apiCustomer.Code),
      name: valueOr(apiCustomer.Name),
    };
  }

  private static mapApiUserManagement(
    apiUserManagement: UserManagementModel
  ): UserManagementDetails {
    return {
      firstName: valueOr(apiUserManagement.FirstName),
      lastName: valueOr(apiUserManagement.LastName),
      phoneNumber: valueOr(apiUserManagement.Phone),
      email: valueOr(apiUserManagement.Email),
      displayName: valueOr(apiUserManagement.Name),
      userId: valueOr(apiUserManagement.UserId),
      createdDateTime: apiUserManagement.CreatedDateTime,
      cardCode: valueOr(apiUserManagement.CardCode),
      permissions: apiUserManagement.Permissions,
      status: apiUserManagement.Status,
    };
  }

  static mapUserDetailsToUserManagementModal(
    userDetails: UserRequestDetails
  ): UserManagementModel {
    return {
      Email: userDetails.email,
      FirstName: userDetails.firstName,
      LastName: userDetails.lastName,
      Name: userDetails.displayName,
      Phone: userDetails.phoneNumber,
      CardCode: userDetails.cardCode,
    };
  }

  getCustomerList(): Promise<
    TransformedApiResponse<CustomerModelODataResultCollection, Customer[]>
  > {
    return this.api
      .apiCustomerGet({
        $orderby: [
          sortToString({ key: "Name", direction: "asc" }),
          sortToString({ key: "Code", direction: "asc" }),
        ].join(","),
      })
      .then(apiResponseTransformer)
      .then(({ value, ...data }) => {
        if (!data) {
          throw new Error("Customers not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(
            "Unable to load customers",
            data.statusCode
          );
        }
        return {
          ...data,
          value: value?.map(CustomerService.mapApiCustomer) ?? [],
        };
      });
  }

  fetchPermissions(
    userId: string | undefined,
    cardCode: string | undefined
  ): Promise<
    TransformedApiResponse<
      UserCustomerPermissionModelODataResultCollection,
      Record<string, boolean>
    >
  > {
    const { compare } = filterFactories;
    return this.userCustomerPermissionApi
      .apiUserCustomerPermissionGet({
        $filter: andFilters([
          compare("UserId").eq(userId),
          compare("CardCode").eq(cardCode),
        ]),
      })
      .then(apiResponseTransformer)
      .then(({ value, ...data }) => {
        if (!data) {
          throw new Error("User permissions not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(
            "Unable to load user permissions",
            data.statusCode
          );
        }
        const permissionEntries = value
          ?.map((permission) => {
            switch (permission.PermissionCode) {
              case IRM_PERMISSIONS.ADD_EDIT_ORDERS:
                return [
                  [ORDERS.EDIT, true],
                  [ORDERS.ADD, true],
                ];
              case IRM_PERMISSIONS.USER_MANAGEMENT:
                return [[USER_MANAGEMENT.VIEW, true]];
              case IRM_PERMISSIONS.SIGN_CONTRACTS:
                return [[ESIGN.SIGN, true]];
              case IRM_PERMISSIONS.REQUEST_CONTRACT:
                return [[CONTRACT.REQUEST, true]];
              default:
                return undefined;
            }
          })
          .flat(1)
          .filter((permission) => permission !== undefined);

        return {
          ...data,
          value: Object.fromEntries(
            (permissionEntries as [string, boolean][]) ?? []
          ),
        };
      });
  }

  getUserManagementRoles(
    cardCode: string,
    searchTerm?: string,
    activeSort?: {
      key: keyof UserManagementModel;
      direction: SORT_DIRECTION;
    }
  ): Promise<
    TransformedApiResponse<
      UserManagementModelODataResultCollection,
      UserManagementDetails[]
    >
  > {
    const { compare, contains } = filterFactories;

    return this.userManagementApi
      .apiUserManagementGet({
        $filter: andFilters([
          compare("CardCode").eq(cardCode),
          searchTerm
            ? orFilters([
                contains("Name", searchTerm),
                contains("Email", searchTerm),
              ])
            : null,
        ]),
        $orderby: sortToString(activeSort),
      })
      .then(apiResponseTransformer)
      .then(({ value, ...data }) => {
        if (!data) {
          throw new Error("User management roles not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(
            "Unable to load user management roles",
            data.statusCode
          );
        }

        return {
          ...data,
          value: value?.map(CustomerService.mapApiUserManagement) ?? [],
        };
      });
  }

  verifyEmailExists(
    email: string,
    cardCode: string | undefined
  ): Promise<ApiResponse<UserManagementModelODataResultCollection>> {
    const { compare } = filterFactories;

    return this.userManagementApi
      .apiUserManagementEmailEmailGet({
        email,
        $filter: compare("CardCode").eq(cardCode),
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Error in verifying email");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }

        return data;
      });
  }

  createNewUser(
    newUserDetails: UserRequestDetails,
    permissions: UserPermissionsFlags
  ): Promise<UserManagementModel> {
    // TODO: add ajv request validation
    const values: UserManagementModel = {
      ...CustomerService.mapUserDetailsToUserManagementModal(newUserDetails),
      Permissions: permissionsObjectToPermissionValues(permissions),
    };

    return this.userManagementApi
      .apiUserManagementCreatePost({
        apiUserManagementCreatePostRequest: values,
      })
      .then((response) => {
        return response.data;
      });
  }

  resendInvite(userId: string, cardCode: string): Promise<string | boolean> {
    return this.userManagementApi
      .apiUserManagementReInviteIdPost({ id: userId, cardCode })
      .then((response) => {
        return response.data;
      });
  }

  removeUser(userId: string, cardCode: string): Promise<boolean> {
    return this.userManagementApi
      .apiUserManagementUserIdDeleteCardCodeCardCodeDelete({
        userId,
        cardCode,
      })
      .then((data) => {
        if (invalidStatusCode(data.status)) {
          throw new ApiStatusException("Unable to remove user", data.status);
        }

        return data.data;
      });
  }

  updateUserPermissions(
    userId: string,
    userDetails: UserRequestDetails,
    permissions: UserPermissionsFlags
  ): Promise<UserManagementModel> {
    // TODO: add ajv request validation
    const values: UserManagementModel = {
      ...CustomerService.mapUserDetailsToUserManagementModal(userDetails),
      UserId: userId,
      Permissions: permissionsObjectToPermissionValues(permissions),
    };

    return this.userManagementApi
      .apiUserManagementEditPost({ apiUserManagementCreatePostRequest: values })
      .then((data) => {
        if (!data) {
          throw new Error("Failed to update user");
        } else if (invalidStatusCode(data.status)) {
          throw new ApiStatusException(data.statusText, data.status);
        }

        return data.data;
      });
  }
}

export default CustomerService;
